import React, { useRef, useState, useEffect, useContext, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { Modal, Spin, message, Button } from "antd";

import { connect as dvaConnect } from "dvajs";
import { useTranslation } from "react-i18next";

import log from "@/utils/logger";
import { FocusSDK, getClientType, isFocusEnv } from "@/utils";
import IconFont from "@/components/icon";
import AudioMp3 from "@/assets/toy_mono.mp3";
import { changeEgovUrl } from "@/utils/tools";
import { handleMeetingSendMessage, joinMeeting } from "@/baseComponents/Meeting/utils";
import { AVSDK, BasicUserInfo, SessionStateEnum } from "@/baseComponents/Meeting/common";
import config, { ConfigEnum, ChatConfig } from "@/config/config";
import "./index.less";
import bus from "@/utils/bus";

// const antIcon = <LoadingOutlined style={{ fontSize: 30 }} spin />;

const MeetingReceiveModal = () => {
  const [visible, setVisible] = useState(false);

  const [connect, setConnect] = useState(true);
  const audioRef = useRef<any>(null);
  const timeRef = useRef<any>(null);
  const [t] = useTranslation("common");
  const [message, setMessage] = useState<any>({});
  const [isCalling, setIsCalling] = useState(false);

  const closeModal = useCallback(() => {
    if (!visible) {
      return;
    }
    setVisible(false);
    setConnect(false);
    setIsCalling(false);
  }, [visible]);

  useEffect(() => {
    function handle(data: any) {
      log.debug("jd meeting receive data:", data);
      setMessage(data);
      setConnect(false);
      setVisible(true);
      setIsCalling(true);
    }
    if (!isFocusEnv()) {
      bus.on("jdmeeting:show-receive-modal", handle);
      bus.on("jdmeeting:hide-receive-modal", closeModal);
    }
    return () => {
      bus.off("jdmeeting:show-receive-modal", handle);
      bus.off("jdmeeting:hide-receive-modal", closeModal);
    };
  }, [closeModal]);

  function onCancel() {
    closeModal();
  }

  const handleClose = useCallback(() => {
    stopAudio();
  }, []);

  const replayMessage = useCallback(
    (sessionState) => {
      const { meetingNumber, meetingPassword, meetingTopic } = message.ext;
      const fromUser: BasicUserInfo = message.ext.fromUser;
      if (!isFocusEnv()) {
        handleMeetingSendMessage({
          avsdk: message?.ext?.avsdk || AVSDK.JBRTC,
          meetingInfo: {
            meetingTopic: meetingTopic,
            meetingNumber,
            password: meetingPassword || ""
          },
          sessionState,
          toUsers: [
            {
              pin: fromUser.pin,
              teamId: fromUser.teamId,
              name: fromUser.name,
              app: fromUser.app,
              avatar: fromUser.avatar
            }
          ]
        }).then(() => {
          closeModal();
        });
      }
      setTimeout(() => {
        handleClose();
      }, 300);
    },
    [handleClose, closeModal, message.ext]
  );

  // 进入页面后，60s后会报message不存在的error，需要加参数处理
  useEffect(() => {
    function handleTimePass() {
      const timer = timeRef.current;
      let intervals = 1000 * 60;
      if (timer) {
        clearTimeout(timer);
      }
      timeRef.current = setTimeout(() => {
        setIsCalling(false);
        replayMessage(SessionStateEnum.TIMEOUT);
      }, intervals);
    }
    if (isCalling) {
      handleTimePass();
    }
  }, [message, isCalling, replayMessage]);

  function stopAudio() {
    const au = audioRef.current;
    if (au) {
      au.muted = true;
    }
  }

  function handleReject() {
    setIsCalling(false);
    replayMessage(SessionStateEnum.UserReject);
  }

  function onJoinMeeting(opts: {
    avsdk: AVSDK;
    meetingNumber: string;
    meetingPassword: string;
    isvideooff: boolean;
    isaudiooff: boolean;
  }) {
    joinMeeting(opts.avsdk, {
      meetingNumber: opts.meetingNumber,
      meetingPassword: opts.meetingPassword,
      isaudiooff: opts.isaudiooff,
      isvideooff: opts.isvideooff
    });
    replayMessage(SessionStateEnum.UserReceive);
  }

  function handleReceVideo() {
    stopAudio();
    const { meetingNumber, meetingPassword, avsdk } = message.ext;

    onJoinMeeting({
      avsdk: avsdk,
      meetingNumber,
      meetingPassword,
      isvideooff: false,
      isaudiooff: true
    });
  }
  function handleReceAudio() {
    stopAudio();
    const { meetingNumber, meetingPassword, avsdk } = message.ext;

    onJoinMeeting({
      avsdk: avsdk,
      meetingNumber,
      meetingPassword,
      isvideooff: true,
      isaudiooff: true
    });
  }

  const showVideoCall = useMemo(() => {
    const { ext = {} } = message;
    return !ext.enableJRTCVoice && config[ChatConfig.CHAT_SESSION_CREATE_MEETING_ENABLE];
  }, [message]);

  if (!message || connect) {
    return null;
  }

  const { ext } = message;
  if (!visible) {
    return null;
  }
  if (!ext?.avsdk) {
    return null;
  }
  const { name } = ext.fromUser;

  return (
    <Modal
      className="web-jd-meeting-call-modal"
      width={580}
      visible={visible}
      maskClosable={false}
      onCancel={onCancel}
      bodyStyle={{
        padding: 0
      }}
      destroyOnClose
      footer={null}
    >
      <div className="receive-meeting-browser">
        <div className="meeting-header">
          <div className="meeting-header-container">
            <div className="meeting-header-content">
              <div className="title">
                <span className="title-name">{name}</span>
              </div>
              <div className="desc">
                {t("joinMeeting.Invite you to an online meeting")}[{ext?.meetingTopic}]
              </div>
            </div>
          </div>
        </div>
        <div className="meeting-action">
          <div className="meeting-action-reject">
            <div className="action-icon" onClick={handleReject}>
              <IconFont type="iconapp_btn_joymerting_hangup" />
            </div>
            <div className="label">{t("joinMeeting.reject")}</div>
          </div>
          {showVideoCall && (
            <div className="meeting-action-video">
              <div className="action-icon" onClick={handleReceVideo}>
                <IconFont type="iconapp_btn_joymerting_video" />
              </div>
              <div className="label">{t("joinMeeting.rece-video")}</div>
            </div>
          )}

          <div className="meeting-action-audio">
            <div className="action-icon" onClick={handleReceAudio}>
              <IconFont type="iconapp_btn_joymerting_answer" />
            </div>
            <div className="label">{t("joinMeeting.rece-audio")}</div>
          </div>
        </div>
        <audio src={changeEgovUrl(AudioMp3)} loop autoPlay ref={audioRef} />
      </div>
    </Modal>
  );
};

function mapStateToProps({ netStatus }: any) {
  return {
    newPublicResult: netStatus.newPublicResult,
    newPrivateResult: netStatus.newPrivateResult
  };
}

function mapDispatchToProps(dispatch: any) {
  return {};
}

export default dvaConnect(mapStateToProps, mapDispatchToProps)(MeetingReceiveModal);
